import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import useForm from 'assets/components/form/hooks/Form';
import Icon from 'assets/components/icon/Icon';
import FileInputSimple from 'assets/components/inputs/file/FileInputSimple';
import MultilineTextInput from 'assets/components/inputs/multilineText/MultilineTextInput';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import Modal from 'assets/components/page/templates/Modal';
import ThemeModalDefault from 'assets/components/page/templates/themes/Theme.Modal.Default';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { formatNumberWithSeparator } from 'assets/utils/data/Number';
import { first, groupBy, isEmpty, keys, sortBy, sumBy } from 'lodash';
import ClaimJobItemPayee from 'models/claims/claimJobItemPayee/Model.ClaimJobItemPayee';
import ClaimTypeDocument from 'models/claims/claimTypeDocument/Model.ClaimTypeDocument';
import PaymentStatus from 'models/enums/PaymentStatus';
import ClaimDocumentForm from 'module/public-web/views/claimSubmit/components/Claim.DocumentForm';
import { useEffect, useMemo } from 'react';
import ClaimDocumentPreview from '../other/Claim.DocumentPreview';
import ClaimJobDataPointsInputs from '../other/Claim.Job.DataPoints.Inputs';
import useClaimSimpleClaim from './Claim.SimpleClaim.Hooks';

function OneDocForm(props: {
  claimTypeDocument: ClaimTypeDocument;
  form: Hooks.Form.Export<
    Api.Core.Claim.SaveSimple.IRequest & {
      uploadableClaimTypeDocuments: Model.IClaimTypeDocument[];
    }
  >;
}) {
  const { lang } = getLocales();
  const form = props.form;

  const docForm = useForm<Utils.FormData<Model.IClaimJobDocument>>({
    default: {
      claimTypeDocumentId: props.claimTypeDocument.id,
      approved: !props.claimTypeDocument.approvalRequired,
    },
    onSubmit: async (data) => {
      const updatedClaimJobs = first(form.data.claimJobs);
      updatedClaimJobs.claimJobDocuments = [...(updatedClaimJobs.claimJobDocuments ?? []), data];
      form.update({ claimJobs: [updatedClaimJobs] });
      docForm.reload();
    },
    validation: (data, errors) => {
      if (!data.name) errors.name = lang.required;
      if (!data.formFile)
        errors.formFile = {
          _objectError: lang.required,
        };
    },
  });

  const uploadedFile = useMemo(
    () =>
      !docForm.data?.formFile
        ? null
        : {
            original: docForm.data.formFile,
            type: docForm.data.formFile.type as Utils.File.MimeType,
            dataUrl: window.URL.createObjectURL(docForm.data.formFile),
            name: docForm.data.name,
            notes: docForm.data.notes,
          },
    [docForm.data.formFile]
  );

  const hasUploadedDocs = first(form.data.claimJobs)?.claimJobDocuments?.some(
    (it) => it.claimTypeDocumentId === props.claimTypeDocument.id
  );
  const allowMoreUploads = !hasUploadedDocs ? true : !!props.claimTypeDocument.allowMultiple;

  return (
    <InputWrapper style={{ marginBottom: Theme.Size.L }}>
      <FileInputSimple
        name="file"
        value={uploadedFile}
        onChange={({ file }) => {
          docForm.update({
            formFile: file.original,
            name: file.original?.name,
          });
        }}
        label={props.claimTypeDocument.name}
        error={docForm.errors?.formFile?._objectError}
        disabled={!allowMoreUploads}
      />
      <TextInput
        name="name"
        label={lang.name}
        value={docForm.data?.name}
        error={docForm.errors?.name}
        onChange={docForm.update}
        disabled={!uploadedFile}
      />
      {props.claimTypeDocument?.allowNotes && (
        <TextInput
          name="notes"
          label={lang.notes}
          value={docForm.data?.notes}
          onChange={docForm.update}
          disabled={!uploadedFile}
        />
      )}
      <Button
        render={lang.add}
        onClick={docForm.submit}
        disabled={docForm.hasErrors}
        active={!docForm.hasErrors}
        class={ThemeButtonCircle}
        style={{ flexShrink: 0 }}
      />
    </InputWrapper>
  );
}
export default function ClaimSimpleClaimForm(props: { contract: Model.IContract; onClose: () => void }) {
  const { lang } = getLocales();
  const { form, claimTypes, onlyClaimType, claimTypeValue, uploadableDocuments, areAutomatedJobItemsEnabled, dealer } =
    useClaimSimpleClaim(props);
  const claimJobForUpdate = first(form.data?.claimJobs);
  function updateClaimJob(claimJob: Utils.FormData<Model.IClaimJob>) {
    form.merge({ claimJobs: [{ ...claimJob }] });
  }
  console.log(form);
  //#region DataPoints
  const updateValue = (value: string, id: number) => {
    const updatedDataPoints = first(form.data?.claimJobs)?.dataPoints?.map((item) =>
      item.claimTypeDataPointId === id ? { ...item, value } : item
    );
    updateClaimJob({ ...first(form.data?.claimJobs), dataPoints: updatedDataPoints });
  };
  useEffect(() => {
    const updatedClaimJob = first(form.data?.claimJobs);
    const newDataPoints = sortBy(
      claimTypes
        ?.find((it) => it.id === claimJobForUpdate?.claimTypeId)
        ?.claimTypeDataPoints?.map((it) => ({
          claimTypeDataPointId: it.id,
          claimTypeId: it.claimTypeId,
          name: it.name,
          type: it.type,
          version: it.version,
          isRequired: it.isRequired,
          value: undefined,
        })),
      (it) => it.claimTypeDataPointId
    );

    updatedClaimJob.dataPoints = newDataPoints;

    updateClaimJob({ ...updatedClaimJob });
  }, [first(form.data?.claimJobs)?.claimTypeId]);
  //#endregion

  const updateJobItem = (fieldName: keyof Model.IClaimJobItem, value: any, templateId: number) => {
    const updatedJobItems = first(form.data?.claimJobs)?.claimJobItems?.map((item) => {
      if (item.templateId !== templateId) return item;

      const adj = fieldName === 'adjudication' ? value : item.adjudication ?? 0.0;
      const gst = fieldName === 'gstHst' ? value : item.gstHst ?? 0.0;
      const pst = fieldName === 'provincialSalesTax' ? value : item.provincialSalesTax ?? 0.0;

      return {
        ...item,
        [fieldName]: value,
        correction: adj,
        total: Number((adj + adj * (gst + pst)).toFixed(2)),
      };
    });

    updateClaimJob({ ...first(form.data?.claimJobs), claimJobItems: updatedJobItems });
  };

  useEffect(() => {
    if (form.data?.dealerId && areAutomatedJobItemsEnabled) {
      const foundClaimType = claimTypes?.find((it) => it.id === claimJobForUpdate?.claimTypeId);

      const tax = foundClaimType?.products
        ?.find((it) => it.id === form.data.contract.productId)
        ?.productTaxes?.find((it) => {
          return it.provinceCode === dealer?.provinceCode;
        });

      const newJobItems = foundClaimType?.claimTypeJobItemTemplates?.map((it) => ({
        //#region Additional fields only for item update
        templateId: it.id,
        lossCodes: it.lossCodes,
        //#endregion
        claimJobItemPayee: new ClaimJobItemPayee({ dealer: dealer, dealerId: dealer?.id }),
        jobItemTypeCode: it.jobItemTypeCode,
        adjudication: it.adjudication,
        lossCode: it.lossCodes.length === 1 ? first(it.lossCodes)?.code : undefined,
        gstHst: tax?.claimsGstHst || 0.0,
        provincialSalesTax: tax?.claimsProvincialSalesTax || 0.0,
        total: it.adjudication + it.adjudication * (tax?.claimsGstHst + tax?.claimsProvincialSalesTax),
        statusCode: PaymentStatus.unpaid,
        quantity: 0,
        hoursWorked: 0,
        correction: it.adjudication,
        labourRate: dealer?.labourRate,
        paymentMethodCode: dealer?.paymentMethodCode,
        isCreatedAutomatically: true,
        currency: 'CAD',
      }));

      claimJobForUpdate.claimJobItems = newJobItems;

      updateClaimJob({ ...claimJobForUpdate });
    } else {
      claimJobForUpdate.claimJobItems = [];
      updateClaimJob({ ...claimJobForUpdate });
    }
  }, [claimJobForUpdate?.claimTypeId, form.data?.dealerId]);
  const groupedClaimJobItems = groupBy(claimJobForUpdate?.claimJobItems, (it) => it.jobItemTypeCode);

  return (
    <Modal
      subcomponents={ThemeModalDefault}
      header={lang.createSimpleClaim}
      onClose={props.onClose}
      render={
        <FormWrapper>
          {!onlyClaimType && (
            <SelectInput
              name="claimTypeId"
              label={lang.claimType}
              data={claimTypes?.map((it) => it.displayInfo)}
              value={claimTypeValue}
              onChange={({ claimTypeId }) => updateClaimJob({ ...claimJobForUpdate, claimTypeId })}
              icon={<Icon class="fas-clipboard-list" />}
              error={!claimTypeValue && lang.required}
            />
          )}
          <MultilineTextInput
            name="customerComplaint"
            label={lang.customerComplaint}
            value={claimJobForUpdate?.customerComplaint}
            onChange={({ customerComplaint }) => updateClaimJob({ ...claimJobForUpdate, customerComplaint })}
          />
          <MultilineTextInput
            name="cause"
            label={lang.causeOfFailure}
            value={claimJobForUpdate?.cause}
            onChange={({ cause }) => updateClaimJob({ ...claimJobForUpdate, cause })}
          />
          <MultilineTextInput
            name="correction"
            label={lang.correctionDetails}
            value={claimJobForUpdate?.correction}
            onChange={({ correction }) => updateClaimJob({ ...claimJobForUpdate, correction })}
          />
          {!!uploadableDocuments?.length && (
            <div>
              <h2>{`${lang.claim} ${lang.documents}`}</h2>
              {!isEmpty(
                form?.data?.uploadableClaimTypeDocuments?.filter(
                  (doc) =>
                    doc.required &&
                    !new Set(
                      first(form?.data.claimJobs)?.claimJobDocuments?.map((doc) => doc.claimTypeDocumentId)
                    )?.has(doc.id)
                )
              ) &&
                uploadableDocuments?.some(
                  (doc) =>
                    doc.required && !first(form.data.claimJobs)?.claimJobDocuments?.some((cjd) => cjd.id === doc.id)
                ) && (
                  <p
                    style={{
                      backgroundColor: Theme.Color.error,
                      color: 'white',
                      padding: `${Theme.Size.M} ${Theme.Size.L}`,
                      fontWeight: 'bold',
                    }}
                  >
                    {`${lang.requiredFilesForTheFollowingDocs}: ${uploadableDocuments
                      .filter(
                        (doc) =>
                          doc.required &&
                          !first(form.data.claimJobs)?.claimJobDocuments?.some((cjd) => cjd.id === doc.id)
                      )
                      .map((doc) => doc.name)
                      .join(', ')}`}
                  </p>
                )}
              <br />

              <DataMap
                data={uploadableDocuments}
                render={({ data: doc }) => <OneDocForm key={doc.id} claimTypeDocument={doc} form={form} />}
              />
              <br />
              <ClaimDocumentPreview
                isDownloadDisabled
                documents={first(form.data?.claimJobs)?.claimJobDocuments?.map((it, docIdx) => ({
                  id: it.id,
                  name: it.name,
                  mimeType: it.mediaFile?.contentType,
                  storedFileName: it.mediaFile?.name,
                  description: it.notes,
                  approved: it.approved,
                  documentName:
                    uploadableDocuments.find((ud) => ud.id === it.claimTypeDocumentId)?.name ?? lang.document,
                  documentType: lang.document,
                  onDelete: () => {
                    const tempDocs = [...(first(form.data?.claimJobs)?.claimJobDocuments || [])];
                    const newDocs = tempDocs?.filter((_, tempIdx) => docIdx !== tempIdx);
                    const updatedClaimJobs = [...(form.data?.claimJobs || [])];
                    first(updatedClaimJobs).claimJobDocuments = [...newDocs];
                    form.merge({ claimJobs: updatedClaimJobs });
                  },
                }))}
              />
              <br />
            </div>
          )}
          {!!first(form.data?.claimJobs)?.dataPoints?.length && (
            <div>
              <h2 style={{ display: 'flex', justifyContent: 'space-between' }}>{lang.details}</h2>
              <ClaimJobDataPointsInputs dataPoints={first(form.data?.claimJobs)?.dataPoints} onUpdate={updateValue} />
            </div>
          )}
          {!!first(form.data?.claimJobs)?.claimJobItems?.length &&
            form.data?.dealerId &&
            areAutomatedJobItemsEnabled && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: Theme.Size.L,
                  marginLeft: Theme.Size.L,
                  marginRight: Theme.Size.L,
                }}
              >
                <h2 style={{ display: 'flex', justifyContent: 'space-between' }}>{lang.jobItemTemplates}</h2>
                {keys(groupedClaimJobItems)?.map((it) => (
                  <div key={it} style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ marginLeft: Theme.Size.XL, marginRight: Theme.Size.XL }}>
                      <h4>{it}</h4>
                    </div>
                    <table className={ThemeTableSimple} style={{ fontSize: Theme.FontSize.S }}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th style={{ textAlign: 'center' }}>{lang.payee}</th>
                          <th style={{ textAlign: 'center' }}>{lang.lossCode}</th>
                          <th style={{ textAlign: 'center' }}>Adjusted amt</th>
                          <th style={{ textAlign: 'center' }}>GST/HST</th>
                          <th style={{ textAlign: 'center' }}>PST</th>
                          <th style={{ textAlign: 'center' }}>{lang.total}</th>
                          <th style={{ display: 'none' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        <DataMap
                          data={groupedClaimJobItems[it]}
                          render={({ data: item, index }) => {
                            return (
                              <tr key={index + item.currency}>
                                <td>{index + 1}</td>
                                <td style={{ textAlign: 'center' }}>{item.claimJobItemPayee?.dealer?.name}</td>
                                <td>
                                  <SelectInput
                                    name="lossCode"
                                    data={item.lossCodes?.map((it) => ({
                                      id: it.code,
                                      title: it.descnEn,
                                    }))}
                                    value={item?.lossCode}
                                    disabled={item.lossCodes?.length === 1}
                                    onChange={({ lossCode }) => updateJobItem('lossCode', lossCode, item.templateId)}
                                    error={!item.lossCode && lang.required}
                                  />
                                </td>
                                <td>
                                  <NumberInput
                                    name="adjudication"
                                    value={Number(item?.adjudication?.toFixed(2))}
                                    onChange={({ adjudication }) =>
                                      updateJobItem('adjudication', adjudication, item.templateId)
                                    }
                                    step={0.01}
                                    style={{ input: { textAlign: 'right' } }}
                                    icon={<Icon>{item.currency}</Icon>}
                                  />
                                </td>
                                <td>
                                  <NumberInput
                                    name="gstHst"
                                    value={Number(((item?.gstHst || 0.0) * item.adjudication).toFixed(2))}
                                    onChange={({ gstHst }) =>
                                      updateJobItem('gstHst', gstHst / item.adjudication, item.templateId)
                                    }
                                    step={0.01}
                                    error={
                                      item?.gstHst > 1 &&
                                      lang.mustBeEqualOrLessThanAmount.replace(
                                        '{amount}',
                                        `Adjusted amt: ${item.adjudication.toFixed(2)}`
                                      )
                                    }
                                    style={{ input: { textAlign: 'right' } }}
                                    icon={<Icon>{item.currency}</Icon>}
                                  />
                                </td>
                                <td>
                                  <NumberInput
                                    name="provincialSalesTax"
                                    value={Number(((item?.provincialSalesTax || 0.0) * item.adjudication).toFixed(2))}
                                    onChange={({ provincialSalesTax }) =>
                                      updateJobItem(
                                        'provincialSalesTax',
                                        provincialSalesTax / item.adjudication,
                                        item.templateId
                                      )
                                    }
                                    error={
                                      item?.provincialSalesTax > 1 &&
                                      lang.mustBeEqualOrLessThanAmount.replace(
                                        '{amount}',
                                        `Adjusted amt: ${item.adjudication.toFixed(2)}`
                                      )
                                    }
                                    step={0.01}
                                    style={{ input: { textAlign: 'right' } }}
                                    icon={<Icon>{item.currency}</Icon>}
                                  />
                                </td>
                                <td style={{ padding: 0 }}>
                                  <NumberInput
                                    name="total"
                                    value={Number(item.total.toFixed(2))}
                                    readOnly
                                    onChange={({ total }) => updateJobItem('total', total, item.templateId)}
                                    style={{ input: { textAlign: 'right' } }}
                                    icon={<Icon>{item.currency}</Icon>}
                                  />
                                </td>
                                <td style={{ display: 'none' }}></td>
                              </tr>
                            );
                          }}
                        />
                      </tbody>
                    </table>
                  </div>
                ))}
                <div>
                  <table className={ThemeTableSimple} style={{ gridColumn: '-1/1', fontSize: Theme.FontSize.S }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'center', fontSize: Theme.FontSize.L }}>{lang.totalAmountWithTaxes}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ textAlign: 'center', fontSize: Theme.FontSize.L }}>
                          {formatNumberWithSeparator(
                            sumBy(claimJobForUpdate.claimJobItems, (it) => it.total).toFixed(2)
                          )}{' '}
                          CAD
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          <h2>{lang.documents}</h2>
          <ClaimDocumentForm
            onSubmit={(data) => form.update({ claimDocuments: [...form.data.claimDocuments, data] })}
          />
          <br />
          <ClaimDocumentPreview
            documents={[
              ...form.data.claimDocuments.map((it, docIdx) => ({
                id: it.id,
                url: it.mediaFile?.url,
                name: it?.name,
                extension: it.mediaFile?.extension,
                description: it?.notes,
                info: lang.new,
                documentType: lang.document,
                onDelete: () => {
                  const tempDocs = [...(form.data?.claimDocuments || [])];
                  const newDocs = tempDocs?.filter((_, tempIdx) => docIdx !== tempIdx);
                  form.update({ claimDocuments: newDocs });
                },
              })),
            ]}
          />
          <br />
          <h2 style={{ display: 'flex', alignItems: 'center' }}>{lang.info}</h2>
          <NumberInput
            name="odometerReading"
            label={lang.odometerReading}
            value={form.data?.odometerReading}
            onChange={form.update}
          />
        </FormWrapper>
      }
      footer={
        <>
          <Button
            onClick={form.submit}
            media={<Icon class="fas-save" />}
            render={lang.save}
            active={!form.hasErrors && form.isUpdated}
            disabled={form.hasErrors || !form.isUpdated}
            isLoading={form.isSubmitted}
            class={ThemeButtonCircle}
            htmlElementProps={{ title: lang.save }}
          />
        </>
      }
    />
  );
}
